import React from 'react';
import { cmxTypeguards, commonEnums } from '@codametrix/ui-common';
import classnames from 'classnames';
import { CMXIcon, Icons } from '@codametrix/ui-components';
import './styles/index.scss';
const { EditorIssueLevel } = commonEnums;

type IconNames = keyof typeof Icons;

type VWIconProps = {
  iconName: IconNames;
  iconClass: string;
};

const IconCell: React.FC<VWIconProps> = ({ iconName, iconClass }) => {
  return (
    <div className="qa-scrubber-issues scrubber-issues col-evidence code-indicator-cell">
      <span className="indicator">
        <CMXIcon
          svgIconName={`${iconName}`}
          svgIconSize="20"
          svgIconClass={`${iconClass}`}
        />
      </span>
    </div>
  );
};
/**
 * React component that displays a Header
 * @param medicalCode
 * @param errors
 * @param handleKeyPress
 * @param handleBlurEvent
 * @param handleUnitsClick
 * @param handleCodeDelete
 * @param handleEnableTechPro
 * @param handleDisableTechPro
 * @returns
 */
export const Header = (
  medicalCode: CMxCommonApp.ChargeSessionCode | CMxCommonApp.ZippedCodeBlock,
  techTransaction: CMxCommonApp.TechTransaction,
  errors: AmplifyAPI.AmplifyError[],
  invoice: CMxCommonApp.CodableInvoice,
  codeEdits: CMxCommonApp.CodeEditInputs,
  handleKeyPress: (ev: any) => void,
  handleBlurEvent: (ev: any) => void,
  handleUnitsClick: (ev: any) => void,
  handleCodeDelete: (ev: any) => void,
  handleCodeChecked: (ev: any) => void,
  handleEnableTechPro: (
    transactionCodeChange: CMxCommonApp.TechCodableTransaction
  ) => void,
  handleDisableTechPro: (
    transactionCodeChange: CMxCommonApp.TechCodableTransaction
  ) => void,
  isTimeBasedCode: () => boolean,
  timeBasedCodePopup: () => React.ReactElement
) => {
  if (cmxTypeguards.isZippedCodeBlock(medicalCode)) {
    const zippedCode = medicalCode as CMxCommonApp.ZippedCodeBlock;
    return (
      <div className="transaction-header icd-code transaction-shared-code-list">
        <div className="move-column">
          <span className="code-gripper prevent-click">
            <CMXIcon
              svgIconName="BiomeGripperSVG20"
              svgIconSize="20"
              svgIconClass="gripper"
            />
          </span>
        </div>
        <div className="codes-column">
          {zippedCode.codes.map((medicalCode, idx) => {
            const active = (medicalCode as any).active;
            const classes = classnames([
              'cmx-chip',
              {
                'shared-code-on': active,
                'shared-code-off': !active
              }
            ]);
            // change icon here... turned off for now.
            const iconName = active ? 'BiomeCheckSVG20' : 'BiomeMinusSVG20';

            return (
              <span
                key={medicalCode.code}
                className="icd-code unclickable prevent-click"
                data-dd-action-name={
                  active === true ? '~Toggle code off' : '~Toggle code on'
                }>
                <span className="code-chip">
                  <label>
                    <span className={classes}>
                      <input
                        type="checkbox"
                        style={{ display: 'none' }}
                        name={`${medicalCode.code}`}
                        onClick={handleCodeChecked}
                        className="txn-shared-checkbox"
                        defaultChecked={active}
                      />

                      {active && (
                        <CMXIcon
                          svgIconName={iconName}
                          svgIconSize="12"
                          svgIconClass="shared-icon"
                        />
                      )}

                      <span className="code-text">{medicalCode.code}</span>
                    </span>
                  </label>
                </span>
              </span>
            );
          })}
        </div>
        {/* no descriptions for shared codes */}
        {/* no actions for shared codes */}
      </div>
    );
  } else if (cmxTypeguards.isChargeSessionCode(medicalCode)) {
    const sessionCode = medicalCode as CMxCommonApp.ChargeSessionCode;
    //@ts-ignore
    const shared = sessionCode.shared ?? false;
    const sharedInvoice =
      invoice?.type === commonEnums.InvoiceTypes.SHARED_CODES;
    const uneditableSharedCode = shared && !sharedInvoice;

    const allEdits = codeEdits[sessionCode.code] ?? [];
    const vwErrors = allEdits.some(
      (edit) => edit.level === EditorIssueLevel.ERROR
    );
    const vwWarnings = allEdits.some(
      (edit) => edit.level === EditorIssueLevel.WARNING
    );

    const styleClasses = [
      `transaction-header`,
      {
        'code-errors': errors.some(
          (error) =>
            (error.message_key === 'procedure_code.units.invalid' &&
              sessionCode.codeType === 'procedure') ||
            error.message_key !== 'procedure_code.units.invalid'
        ),
        unitable: sessionCode.unitable,
        editable: sessionCode.editable,
        'uneditable-shared-code': uneditableSharedCode,
        ['transaction-shared-code']: shared
      },
      `${sessionCode.provenance.toLowerCase()}-code`
    ];

    const evidence = sessionCode.evidence ?? [];
    const hasEvidence = evidence.length > 0;
    const description = sessionCode.description || 'Retrieving description...';
    const codeUnitsStyle = [
      'code-units-input',
      {
        'procedure-code-units-invalid': errors.some(
          (error) => error.message_key === 'procedure_code.units.invalid'
        )
      }
    ];

    const showActionsColumn =
      vwErrors ||
      vwWarnings ||
      hasEvidence ||
      techTransaction.hasTechCode ||
      techTransaction.hasProCode;

    const proClassName = classnames([
      'pro-tech-box',
      {
        'is-active': techTransaction.isProCodeActive,
        'is-inactive': !techTransaction.isProCodeActive,
        'single-box': !techTransaction.hasTechCode,
        'left-box': techTransaction.hasTechCode
      }
    ]);

    const techClassName = classnames([
      'pro-tech-box',
      {
        'is-active': techTransaction.isTechCodeActive,
        'is-inactive': !techTransaction.isTechCodeActive,
        'single-box': !techTransaction.hasProCode,
        'right-box': techTransaction.hasProCode
      }
    ]);

    const techIndicatorClassName = classnames([
      'qa-tech-indicator',
      'prevent-click',
      'tech-indicator',
      'indicator',
      'indicator-pro-tech',
      {
        'tech-indicator-no-space':
          (techTransaction.hasProCode && techTransaction.isTechCodeActive) ||
          (techTransaction.hasTechCode && techTransaction.isProCodeActive),
        'indicator-pro-tech-active': techTransaction.isTechCodeActive,
        'indicator-pro-tech-inactive': !techTransaction.isTechCodeActive,
        'single-indicator-pro-tech': !techTransaction.hasProCode,
        'indicator-pro-tech-modifier': techTransaction.isModifier,
        'indicator-pro-tech-no-modifier': !techTransaction.isModifier,
        'pro-tech-loading': techTransaction.isLoading
      }
    ]);

    const proIndicatorClassName = classnames([
      'qa-pro-indicator',
      'prevent-click',
      'pro-indicator',
      'indicator',
      'indicator-pro-tech',
      {
        'indicator-pro-tech-active': techTransaction.isProCodeActive,
        'indicator-pro-tech-inactive': !techTransaction.isProCodeActive,
        'single-indicator-pro-tech': !techTransaction.hasTechCode,
        'indicator-pro-tech-modifier': techTransaction.isModifier,
        'indicator-pro-tech-no-modifier': !techTransaction.isModifier,
        'pro-tech-loading': techTransaction.isLoading
      }
    ]);

    const proCheck = classnames([
      'pro-tech-check',
      {
        'pro-tech-check-active': techTransaction.isProCodeActive,
        'pro-tech-check-inactive': !techTransaction.isProCodeActive
      }
    ]);

    const techCheck = classnames([
      'pro-tech-check',
      {
        'pro-tech-check-active': techTransaction.isTechCodeActive,
        'pro-tech-check-inactive': !techTransaction.isTechCodeActive
      }
    ]);

    const toggleProCode = () => {
      if (!techTransaction.isLoading) {
        techTransaction.isProCodeActive
          ? handleEnableTechPro(techTransaction.proContext)
          : handleDisableTechPro(techTransaction.proContext);
      }
    };

    const toggleTechCode = () => {
      if (!techTransaction.isLoading) {
        techTransaction.isTechCodeActive
          ? handleEnableTechPro(techTransaction.techContext)
          : handleDisableTechPro(techTransaction.techContext);
      }
    };

    return (
      <div
        key={`${sessionCode.code}-${sessionCode.codeType}`}
        data-code={`${sessionCode.code}`}
        data-code-type={`${sessionCode.codeType}`}
        className={classnames(styleClasses)}>
        <div className="units-column">
          {sessionCode.codeType !== 'procedure' ? (
            <span className="code-gripper prevent-click">
              <CMXIcon
                svgIconName="BiomeGripperSVG20"
                svgIconSize="20"
                svgIconClass="gripper"
              />
            </span>
          ) : (
            <span className="code-units-populateable prevent-click">
              <span className="case-units-numeral">
                <input
                  onInput={handleKeyPress}
                  onBlur={handleBlurEvent}
                  onClick={handleUnitsClick}
                  type="text"
                  disabled={!sessionCode.editable}
                  className={classnames(codeUnitsStyle)}
                  value={sessionCode.units}
                />
              </span>
            </span>
          )}
        </div>
        <div className="codes-column">
          <span className="code-chip">
            <span className="cmx-chip">
              <span className="code-text">{sessionCode.code}</span>
            </span>
            {!uneditableSharedCode && (
              <span
                onClick={handleCodeDelete}
                className="code-action-icon code-delete-icon cmx-chip">
                <span className="code-action-icon-inner code-delete">
                  <a href="#/delete-code" className="code-delete-link">
                    <CMXIcon svgIconName="BiomeCancelSVG20" svgIconSize="12" />
                  </a>
                </span>
              </span>
            )}
          </span>
        </div>
        <div
          title={description}
          className="qa-description-column description-column">
          <span className="ellipsify">{description}</span>
        </div>
        <>
          {sessionCode.codeType === 'procedure' &&
            isTimeBasedCode() &&
            timeBasedCodePopup()}
        </>
        {showActionsColumn && (
          <div className="code-actions-column">
            {hasEvidence && (
              <div className="col-evidence code-indicator-cell">
                <span className="indicator">CMX</span>
              </div>
            )}
            {vwErrors && (
              <IconCell
                iconName="BiomeVWErrorSVG20"
                iconClass="qa-issue-error alert-error"
              />
            )}
            {
              /**
               * you can have errors and warnings at the same time.
               * due to the way react code is structured, i really don't like
               * nested ternary operators in jsx, hence the !vwErrors
               */
              !vwErrors && vwWarnings && (
                <IconCell
                  iconName="BiomeVWWarningSVG20"
                  iconClass="qa-issue-warning alert-warning"
                />
              )
            }
            {(techTransaction.hasTechCode || techTransaction.hasProCode) && (
              <>
                {techTransaction.hasProCode && (
                  <span
                    className={classnames(proIndicatorClassName)}
                    onClick={toggleProCode}>
                    <div className={proClassName}>
                      <CMXIcon
                        svgIconName="BiomeCheckSVG20"
                        svgIconSize="12"
                        svgIconClass={classnames(proCheck)}
                      />
                      <p className="pro-tech-label">P</p>
                    </div>
                  </span>
                )}

                {techTransaction.hasTechCode && (
                  <span
                    className={classnames(techIndicatorClassName)}
                    onClick={toggleTechCode}>
                    <div className={techClassName}>
                      <CMXIcon
                        svgIconName="BiomeCheckSVG20"
                        svgIconSize="12"
                        svgIconClass={classnames(techCheck)}
                      />
                      <p className="pro-tech-label">T</p>
                    </div>
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }
};
